import { useEffect, useMemo, useRef, useState } from "react";
import { DeskMarks } from "./DeskMarks";
import { fmt } from "../lib/format";
import { api } from "../lib/api";

const W = 1000;
const PADR = 66;   // right price axis
const PADT = 10;
const PADB = 20;
const TFS = ["5m", "15m", "1h", "4h", "1d"];

export const PriceChart = ({ symbol, market, position, onAdjust, forecast, prognosis, trend, deskOrder, pme, follow, initialMode }) => {
  const [tf, setTf] = useState("15m");
  const [bars, setBars] = useState([]);
  const [cursor, setCursor] = useState(null); // {x, y, price, bar}
  const [preview, setPreview] = useState(null); // {which, price}
  const [zoom, setZoom] = useState(90); // # of visible bars
  const [pan, setPan] = useState(0);    // bars scrolled back from the latest
  const [replay, setReplay] = useState(null);   // index into position.stop_history while replaying the trail
  const hist = position?.stop_history || [];
  useEffect(() => {
    if (replay == null) return undefined;
    if (replay >= hist.length - 1) { const t = setTimeout(() => setReplay(null), 1800); return () => clearTimeout(t); }
    const t = setTimeout(() => setReplay((i) => i + 1), Math.max(250, 3500 / Math.max(1, hist.length)));
    return () => clearTimeout(t);
  }, [replay, hist.length]);
  const [drawMode, setDrawMode] = useState(false);
  const [layers, setLayers] = useState(() => {
    try { return { zones: true, fvg: true, sweep: true, scenario: true, desk: true, ...JSON.parse(localStorage.getItem("chart_layers") || "{}") }; }
    catch { return { zones: true, fvg: true, sweep: true, scenario: true, desk: true }; }
  });
  useEffect(() => { try { localStorage.setItem("chart_layers", JSON.stringify(layers)); } catch {} }, [layers]);
  const [mode, setMode] = useState(() => { if (initialMode) return initialMode; try { return localStorage.getItem("chart_mode_v2") || "LIQUID"; } catch { return "LIQUID"; } });
  useEffect(() => { if (initialMode) return; try { localStorage.setItem("chart_mode_v2", mode); } catch {} }, [mode, initialMode]);
  const regime = forecast?.regime || market?.regime || null;
  const [lines, setLines] = useState([]);       // [{a:{ts,price}, b:{ts,price}}]
  const [pending, setPending] = useState(null);  // first click point while drawing
  const svgRef = useRef(null);
  const panningRef = useRef(false);
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  const lenRef = useRef(0);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);
  useEffect(() => { lenRef.current = bars.length; }, [bars.length]);

  // cinematic liquid canvas layer (drawn behind the SVG overlays in LIQUID mode)
  const wrapRef = useRef(null);
  const liquidCanvasRef = useRef(null);
  const [csize, setCsize] = useState({ w: 0, h: 0 });
  // svg-space height follows the real aspect ratio so labels never stretch on the big chart
  const H = csize.w && csize.h ? Math.round(Math.max(360, Math.min(1100, (W * csize.h) / csize.w))) : 460;
  const PLOTH = H - PADT - PADB;
  const drawRef = useRef({ view: null, mode: "LIQUID", up: true });
  const particlesRef = useRef([]);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((e) => { const r = e[0].contentRect; setCsize({ w: r.width, h: r.height }); });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // reset zoom/pan when the symbol or timeframe changes
  useEffect(() => { setZoom(90); setPan(0); setPending(null); }, [symbol, tf]);

  // load/save user trendlines per symbol
  useEffect(() => {
    try { setLines(JSON.parse(localStorage.getItem(`trendlines:${symbol}`) || "[]")); }
    catch { setLines([]); }
    setPending(null);
  }, [symbol]);
  useEffect(() => {
    try { localStorage.setItem(`trendlines:${symbol}`, JSON.stringify(lines)); } catch {}
  }, [lines, symbol]);

  // smooth wheel/trackpad zoom, anchored under the cursor (continuous, fractional)
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const total = lenRef.current;
      if (!total) return;
      const r = el.getBoundingClientRect();
      const svgX = ((e.clientX - r.left) / r.width) * W;      // 0..1000 svg-space
      const z = zoomRef.current, p = panRef.current;
      const count = Math.max(6, Math.min(z, total));
      const maxPan = Math.max(0, total - count);
      const minPan = -Math.round(count * 0.5);
      const pc = Math.max(minPan, Math.min(p, maxPan));
      const start = total - count - pc;
      const pastW = (W - PADR) * 0.74;
      const cw = pastW / count;
      const idxUnder = start + svgX / cw;                     // fractional bar index under cursor
      const factor = Math.max(0.5, Math.min(2, Math.exp(e.deltaY * 0.0015)));
      const nz = Math.max(12, Math.min(400, z * factor));
      const nc = Math.max(6, Math.min(nz, total));
      const ncw = pastW / nc;
      const nStart = idxUnder - svgX / ncw;
      let nPan = total - nc - nStart;
      const nMin = -Math.round(nc * 0.5), nMax = Math.max(0, total - nc);
      nPan = Math.max(nMin, Math.min(nPan, nMax));
      setZoom(nz);
      setPan(nPan);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [bars.length > 0]);

  // fetch bars for the selected symbol + timeframe, then keep them fresh
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const load = () => api.chart(symbol, tf).then((d) => { if (alive) setBars(d.bars || []); }).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [symbol, tf]);

  const levels = market?.levels || [];
  const livePrice = market?.price;
  const wedge = market?.wedge || null;
  const zones = market?.zones || [];
  const fvgs = market?.fvgs || [];
  const sweep = market?.sweep || null;
  const sDir = market?.direction || null;
  const sGrade = market?.grade || null;
  const sEntry = market?.entry;
  const sSL = market?.stop_loss;
  const sTP = market?.take_profit;
  const sTriggered = !!market?.triggered;
  const rev = market?.reversal || null;
  // an actionable scenario: a graded/triggered setup (or any reversal setup) with a full entry/target/stop, and no live
  // position already on this symbol (position markers take over then)
  const scenario = (!position && sDir && sEntry != null && sSL != null && sTP != null &&
    (sGrade === "A" || sGrade === "A+" || sTriggered || rev))
    ? { dir: sDir, grade: sGrade, entry: sEntry, sl: sSL, tp: sTP, rr: market?.rr, triggered: sTriggered }
    : null;
  const tfMin = { "5m": 5, "15m": 15, "1h": 60, "4h": 240, "1d": 1440 }[tf] || 5;

  const view = useMemo(() => {
    const total = bars.length;
    if (!total) return null;
    const count = Math.max(6, Math.min(zoom, total));
    const maxPan = Math.max(0, total - count);
    const minPan = -Math.round(count * 0.5);   // allow pushing the latest bar toward centre
    const p = Math.max(minPan, Math.min(pan, maxPan));
    const start = total - count - p;            // left edge, fractional global index
    const iLo = Math.max(0, Math.floor(start));
    const iHi = Math.min(total, Math.ceil(start + count) + 1);
    const data = bars.slice(iLo, iHi);
    if (!data.length) return null;
    let hi = Math.max(...data.map((b) => b.h));
    let lo = Math.min(...data.map((b) => b.l));
    const natural = (hi - lo) || (hi * 0.002) || 1;
    // must-show levels (live price, working position, actionable setup) may stretch the plot a little;
    // reference levels (HTF S/R ladders, desk marks, sweeps) only if they sit near the visible range
    const must = [];
    const near = [];
    if (position) must.push(position.stop_loss, position.take_profit, position.entry_price);
    if (scenario) must.push(scenario.tp, scenario.sl, scenario.entry);
    if (livePrice) must.push(livePrice);
    levels.forEach((l) => near.push(l.price));
    if (sweep) near.push(sweep.level);
    if (prognosis && prognosis.symbol === symbol) { near.push(prognosis.invalidation, prognosis.target, prognosis.price_at); }
    if (deskOrder && deskOrder.symbol === symbol && deskOrder.status === "WORKING") { must.push(deskOrder.entry, deskOrder.stop_loss, deskOrder.take_profit); }
    (pme?.positions || []).filter((m) => m.symbol === symbol).forEach((m) => { must.push(m.averageEntry, m.currentStop); near.push(m.tp1, m.tp2); });
    must.forEach((pp) => {
      if (pp != null && pp >= lo - natural * 1.0 && pp <= hi + natural * 1.0) { hi = Math.max(hi, pp); lo = Math.min(lo, pp); }
    });
    near.forEach((pp) => {
      if (pp != null && pp >= lo - natural * 0.35 && pp <= hi + natural * 0.35) { hi = Math.max(hi, pp); lo = Math.min(lo, pp); }
    });
    const pad = (hi - lo) * 0.08 || 1;
    hi += pad; lo -= pad;
    const plotW = W - PADR;
    const pastW = plotW * 0.74;                 // reserve the right ~26% as the forecast / future zone
    const y = (pr) => PADT + ((hi - pr) / (hi - lo)) * PLOTH;
    const cw = pastW / count;
    const xOfGlobal = (k) => (k + 0.5 - start) * cw;   // global bar index -> x centre (fractional)
    const xOfLocal = (j) => xOfGlobal(iLo + j);
    return { data, hi, lo, y, cw, plotW, pastW, iLo, start, count, total, xOfGlobal, xOfLocal };
  }, [bars, levels, position, scenario, sweep, livePrice, zoom, pan, prognosis, deskOrder, pme, symbol, PLOTH]);

  const chgUp = (market?.change || 0) >= 0;
  useEffect(() => { drawRef.current = { view, mode, up: chgUp }; }, [view, mode, chgUp]);
  useEffect(() => {
    const cv = liquidCanvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf;
    if (particlesRef.current.length === 0) {
      particlesRef.current = Array.from({ length: 70 }, () => ({ p: Math.random(), sp: 0.001 + Math.random() * 0.0022, sz: 0.6 + Math.random() * 1.6, jit: (Math.random() - 0.5) * 6 }));
    }
    const bubbles = Array.from({ length: 12 }, () => ({ x: Math.random(), y: Math.random(), sp: 0.0006 + Math.random() * 0.0013, r: 1 + Math.random() * 2.6 }));
    const draw = () => {
      const { view: g, mode: md, up } = drawRef.current;
      const w = csize.w, h = csize.h, dpr = window.devicePixelRatio || 1;
      if (!w || !h) { raf = requestAnimationFrame(draw); return; }
      if (cv.width !== w * dpr || cv.height !== h * dpr) { cv.width = w * dpr; cv.height = h * dpr; }
      ctx.setTransform(dpr * w / W, 0, 0, dpr * h / H, 0, 0);   // draw in svg-space (1000x460)
      ctx.clearRect(0, 0, W, H);
      if (!g || md !== "LIQUID" || g.data.length < 2) { raf = requestAnimationFrame(draw); return; }
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#0a0f16"); bg.addColorStop(1, "#05070b");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, g.plotW, H);
      const pts = g.data.map((b, i) => [g.xOfLocal(i), g.y(b.c)]);
      const grad = ctx.createLinearGradient(0, 0, g.pastW, 0);
      grad.addColorStop(0, "#00F0FF"); grad.addColorStop(0.55, "#00E5B5"); grad.addColorStop(1, up ? "#26d07c" : "#FF00FF");
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) { const xc = (pts[i][0] + pts[i - 1][0]) / 2, yc = (pts[i][1] + pts[i - 1][1]) / 2; ctx.quadraticCurveTo(pts[i - 1][0], pts[i - 1][1], xc, yc); }
      ctx.lineTo(pts[pts.length - 1][0], PADT + PLOTH); ctx.lineTo(pts[0][0], PADT + PLOTH); ctx.closePath();
      const fg = ctx.createLinearGradient(0, PADT, 0, PADT + PLOTH);
      fg.addColorStop(0, up ? "rgba(38,208,124,0.28)" : "rgba(255,77,109,0.24)"); fg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = fg; ctx.fill();
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) { const xc = (pts[i][0] + pts[i - 1][0]) / 2, yc = (pts[i][1] + pts[i - 1][1]) / 2; ctx.quadraticCurveTo(pts[i - 1][0], pts[i - 1][1], xc, yc); }
      ctx.strokeStyle = grad; ctx.lineWidth = 2.2; ctx.lineJoin = "round"; ctx.lineCap = "round";
      ctx.shadowColor = up ? "#00E5B5" : "#FF00FF"; ctx.shadowBlur = 14; ctx.stroke(); ctx.shadowBlur = 0;
      const at = (f) => { const t = f * (pts.length - 1); const i = Math.min(pts.length - 2, Math.floor(t)); const fr = t - i; return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * fr, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * fr]; };
      particlesRef.current.forEach((pt) => { pt.p += pt.sp; if (pt.p > 1) pt.p -= 1; const [px, py] = at(pt.p); ctx.beginPath(); ctx.arc(px, py + pt.jit, pt.sz, 0, Math.PI * 2); ctx.fillStyle = up ? "rgba(180,255,240,0.9)" : "rgba(255,190,220,0.9)"; ctx.shadowColor = up ? "#00F0FF" : "#FF00FF"; ctx.shadowBlur = 7; ctx.fill(); ctx.shadowBlur = 0; });
      bubbles.forEach((b) => { b.y -= b.sp; if (b.y < 0) { b.y = 1; b.x = Math.random(); } const bx = b.x * g.pastW, by = PADT + b.y * PLOTH; ctx.beginPath(); ctx.arc(bx, by, b.r, 0, Math.PI * 2); ctx.strokeStyle = "rgba(0,240,255,0.12)"; ctx.lineWidth = 1; ctx.stroke(); });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [csize.w, csize.h]);

  const dec = (p) => (p != null && p > 1000 ? 1 : p != null && p < 2 ? 4 : 2);
  const priceFromClientY = (clientY) => {
    const r = svgRef.current.getBoundingClientRect();
    const svgY = ((clientY - r.top) / r.height) * H;
    return view.hi - ((svgY - PADT) / PLOTH) * (view.hi - view.lo);
  };
  const clamp = (p) => Math.max(view.lo, Math.min(view.hi, p));
  const adjustable = !!(onAdjust && position);

  const startDrag = (which) => (e) => {
    if (!adjustable) return;
    e.preventDefault(); e.stopPropagation();
    const move = (ev) => setPreview({ which, price: clamp(priceFromClientY(ev.clientY)) });
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const price = clamp(priceFromClientY(ev.clientY));
      setPreview(null);
      onAdjust({ [which === "sl" ? "stop_loss" : "take_profit"]: Number(price.toFixed(4)) });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onHover = (e) => {
    if (!view || panningRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const sx = ((e.clientX - r.left) / r.width) * W;
    const sy = ((e.clientY - r.top) / r.height) * H;
    if (sx > view.plotW) { setCursor(null); return; }
    const j = Math.round(view.start + sx / view.cw - view.iLo - 0.5);
    const idx = Math.max(0, Math.min(view.data.length - 1, j));
    setCursor({ x: view.xOfLocal(idx), y: sy, price: priceFromClientY(e.clientY), bar: view.data[idx] });
  };

  // capture a chart point (timestamp + price) from a pointer event, for drawing trendlines
  const pointAtEvent = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const sx = ((e.clientX - r.left) / r.width) * W;
    const j = Math.round(view.start + sx / view.cw - view.iLo - 0.5);
    const idx = Math.max(0, Math.min(view.data.length - 1, j));
    const bar = view.data[idx] || {};
    return { ts: bar.ts ?? bar.t ?? idx, price: Number(priceFromClientY(e.clientY).toFixed(4)) };
  };

  // click-drag to pan — OR, in draw mode, click two points to lay down a trendline
  const startPan = (e) => {
    if (!view) return;
    if (drawMode) {
      const pt = pointAtEvent(e);
      if (!pending) { setPending(pt); }
      else { setLines((ls) => [...ls, { a: pending, b: pt }]); setPending(null); }
      return;
    }
    const r = svgRef.current.getBoundingClientRect();
    const startX = e.clientX;
    const startPanVal = pan;
    const total = bars.length;
    const count = Math.max(6, Math.min(zoom, total));
    const maxPan = Math.max(0, total - count);
    const minPan = -Math.round(count * 0.5);
    const move = (ev) => {
      const dxSvg = ((ev.clientX - startX) / r.width) * W;
      const dBars = dxSvg / view.cw;      // fractional — smooth, sub-candle panning
      if (Math.abs(dBars) > 0.12) { panningRef.current = true; setCursor(null); }
      setPan(Math.max(minPan, Math.min(maxPan, startPanVal + dBars)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setTimeout(() => { panningRef.current = false; }, 0);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const resetView = () => { setZoom(90); setPan(0); };

  // map a stored timestamp to an x-coordinate within the current visible window (linear by avg spacing)
  const tsToX = (ts) => {
    if (!view || !view.data.length) return 0;
    const d = view.data, n = d.length;
    const ts0 = d[0].ts ?? d[0].t ?? 0;
    const tsN = d[n - 1].ts ?? d[n - 1].t ?? (n - 1);
    if (tsN === ts0) return view.xOfLocal(n - 1);
    const idx = ((ts - ts0) / (tsN - ts0)) * (n - 1);
    return view.xOfLocal(idx);
  };

  return (
    <div ref={wrapRef} className="flex-1 min-h-0 relative bg-[#07090d]" data-testid="price-chart-canvas">
      <canvas ref={liquidCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ width: "100%", height: "100%" }} data-testid="liquid-stream-canvas" />
      {/* timeframe tabs */}
      <div className="absolute top-2 left-2 z-20 flex items-center gap-1 rounded-md bg-black/60 backdrop-blur-sm border border-zinc-800 p-0.5">
        {position && hist.length > 1 && (
          <button onClick={() => setReplay(replay == null ? 0 : null)} data-testid="stop-replay-btn" title="Replay how the stop trailed"
            className={`px-1.5 py-0.5 rounded text-[9px] font-mono tracking-[0.14em] border ${replay != null ? "border-amber-400 text-amber-300" : "border-zinc-700 text-zinc-400 hover:text-amber-300"}`}>
            {replay != null ? "■ STOP" : `▶ REPLAY TRAIL (${hist.length})`}
          </button>
        )}
        {TFS.map((t) => (
          <button
            key={t}
            data-testid={`tf-${t}`}
            onClick={() => setTf(t)}
            className={`px-2.5 py-1 rounded text-[10px] font-mono font-semibold transition-colors ${
              tf === t ? "bg-cyan-500/20 text-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.35)]" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >{t.toUpperCase()}</button>
        ))}
        {adjustable && <span className="ml-1 pl-2 border-l border-zinc-800 text-[9px] text-zinc-500 font-mono">drag SL/TP</span>}
      </div>
      {/* zoom controls */}
      <div className="absolute bottom-2 left-2 z-20 flex items-center gap-1 rounded-md bg-black/60 backdrop-blur-sm border border-zinc-800 p-0.5" data-testid="chart-zoom-controls">
        <button data-testid="zoom-out-btn" onClick={() => setZoom((z) => Math.min(400, z * 1.25))} className="px-2 py-1 rounded text-[12px] leading-none font-mono text-zinc-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" title="Zoom out">−</button>
        <button data-testid="zoom-in-btn" onClick={() => setZoom((z) => Math.max(12, z * 0.8))} className="px-2 py-1 rounded text-[12px] leading-none font-mono text-zinc-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" title="Zoom in">+</button>
        <button data-testid="zoom-reset-btn" onClick={resetView} className="px-2 py-1 rounded text-[9px] font-mono text-zinc-500 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors border-l border-zinc-800 ml-0.5" title="Reset view">RESET</button>
        <button data-testid="draw-line-btn" onClick={() => { setDrawMode((d) => !d); setPending(null); }}
          className={`px-2 py-1 rounded text-[9px] font-mono transition-colors border-l border-zinc-800 ml-0.5 ${drawMode ? "text-cyan-300 bg-cyan-500/20" : "text-zinc-500 hover:text-cyan-300 hover:bg-cyan-500/10"}`}
          title="Draw trendline (click two points)">✎ LINE</button>
        <button data-testid="clear-lines-btn" onClick={() => { setLines([]); setPending(null); }}
          className="px-2 py-1 rounded text-[9px] font-mono text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" title="Clear trendlines">CLEAR</button>
      </div>
      <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
        {regime && <span className="rounded-sm bg-cyan-500/15 px-1.5 py-0.5 text-[9px] font-bold text-cyan-300">{regime}</span>}
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
        {symbol} · {market?.source || "SIM"}
      </div>

      {/* chart render mode */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 rounded-md bg-black/60 backdrop-blur-sm border border-zinc-800 p-0.5" data-testid="chart-mode-toggles">
        {["LIQUID", "CANDLE", "LINE", "HYBRID"].map((m) => (
          <button key={m} data-testid={`mode-${m.toLowerCase()}`} onClick={() => setMode(m)}
            className={`px-2 py-1 rounded text-[9px] font-mono font-bold tracking-wide transition-colors ${mode === m ? "bg-cyan-500/20 text-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.35)]" : "text-zinc-600 hover:text-zinc-400"}`}>
            {m}
          </button>
        ))}
      </div>

      {/* SMC overlay layer toggles */}
      <div className="absolute top-9 right-2 z-20 flex items-center gap-1 rounded-md bg-black/60 backdrop-blur-sm border border-zinc-800 p-0.5" data-testid="chart-layer-toggles">
        {[
          { k: "zones", label: "ZONES", col: "text-emerald-300", ring: "bg-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.35)]" },
          { k: "fvg", label: "FVG", col: "text-sky-300", ring: "bg-sky-500/20 shadow-[0_0_10px_rgba(56,189,248,0.35)]" },
          { k: "sweep", label: "SWEEP", col: "text-yellow-300", ring: "bg-yellow-500/20 shadow-[0_0_10px_rgba(234,179,8,0.35)]" },
          { k: "scenario", label: "SETUP", col: "text-fuchsia-300", ring: "bg-fuchsia-500/20 shadow-[0_0_10px_rgba(232,121,249,0.35)]" },
          { k: "desk", label: "DESK", col: "text-cyan-300", ring: "bg-cyan-500/20 shadow-[0_0_10px_rgba(34,211,238,0.35)]" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`layer-${t.k}`}
            onClick={() => setLayers((L) => ({ ...L, [t.k]: !L[t.k] }))}
            className={`px-2 py-1 rounded text-[9px] font-mono font-semibold transition-colors ${
              layers[t.k] ? `${t.ring} ${t.col}` : "text-zinc-600 hover:text-zinc-400"}`}
          >{t.label}</button>
        ))}
      </div>

      {!view ? (
        <div className="flex-1 h-full flex items-center justify-center text-zinc-600 text-xs font-mono">loading {tf} …</div>
      ) : (
        <svg
          ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full relative z-10"
          style={{ cursor: "crosshair", touchAction: "none" }}
          onPointerDown={startPan} onPointerMove={onHover} onPointerLeave={() => setCursor(null)} onDoubleClick={resetView}
        >
          <defs>
            <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0b0f16" />
              <stop offset="100%" stopColor="#07090d" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <clipPath id="plotClip"><rect x="0" y="0" width={W - PADR} height={H} /></clipPath>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill={mode === "LIQUID" ? "transparent" : "url(#bgGrad)"} />

          {/* horizontal grid + price axis */}
          {[...Array(6)].map((_, i) => {
            const p = view.hi - ((view.hi - view.lo) / 5) * i;
            const yy = view.y(p);
            return (
              <g key={`g${i}`}>
                <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke="#12161d" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                <text x={W - PADR + 5} y={yy + 3} fill="#5b6370" fontSize="10.5" fontFamily="JetBrains Mono">{fmt(p, dec(p))}</text>
              </g>
            );
          })}

          {/* ---- HTF Supply / Demand zones (price bands) ---- */}
          {layers.zones && zones.map((z, i) => {
            const top = Math.max(z.high, z.low), bot = Math.min(z.high, z.low);
            if (top < view.lo || bot > view.hi) return null;
            const yT = view.y(Math.min(top, view.hi)), yB = view.y(Math.max(bot, view.lo));
            const supply = z.type === "supply";
            const col = supply ? "#f43f5e" : "#22c55e";
            const strong = z.grade === "LG";
            return (
              <g key={`z${i}`} data-testid={`zone-${i}`}>
                <rect x={0} y={yT} width={view.plotW} height={Math.max(yB - yT, 1.5)} fill={col}
                  opacity={strong ? 0.14 : 0.07} />
                <line x1={0} x2={view.plotW} y1={yT} y2={yT} stroke={col} strokeWidth="1" opacity={strong ? 0.55 : 0.3} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
                <line x1={0} x2={view.plotW} y1={yB} y2={yB} stroke={col} strokeWidth="1" opacity={strong ? 0.55 : 0.3} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
                <text x={view.plotW - 4} y={yT + 10} textAnchor="end" fill={col} fontSize="8.5" fontWeight="700" fontFamily="JetBrains Mono" opacity="0.85">
                  {supply ? "SUPPLY" : "DEMAND"}{strong ? " ★" : ""}
                </text>
              </g>
            );
          })}

          {/* ---- Fair Value Gaps ---- */}
          {layers.fvg && fvgs.map((f, i) => {
            const top = Math.max(f.high, f.low), bot = Math.min(f.high, f.low);
            if (top < view.lo || bot > view.hi) return null;
            const yT = view.y(Math.min(top, view.hi)), yB = view.y(Math.max(bot, view.lo));
            const bull = f.type === "bullish";
            const col = bull ? "#38bdf8" : "#e879f9";
            return (
              <g key={`f${i}`} data-testid={`fvg-${i}`}>
                <rect x={view.plotW * 0.5} y={yT} width={view.plotW * 0.5} height={Math.max(yB - yT, 1.5)}
                  fill={col} opacity="0.10" stroke={col} strokeOpacity="0.35" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
                <text x={view.plotW - 4} y={yB - 3} textAnchor="end" fill={col} fontSize="8" fontWeight="700" fontFamily="JetBrains Mono" opacity="0.8">
                  {f.state === "iFVG" ? "iFVG" : "FVG"}
                </text>
              </g>
            );
          })}

          {/* S/R levels */}
          {levels.map((l, i) => {
            if (l.price < view.lo || l.price > view.hi) return null;
            const yy = view.y(l.price);
            const isWedge = !!l.wedge;
            const col = isWedge ? "#f59e0b" : l.kind === "R" ? "#f43f5e" : "#22c55e";
            const label = isWedge ? (l.wedge === "top" ? "WEDGE▲" : "WEDGE▼") : `${l.kind}${l.strong ? "★" : ""}`;
            return (
              <g key={`lv${i}`} opacity={l.strong || isWedge ? 0.8 : 0.32}>
                <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke={col} strokeWidth="1" strokeDasharray={isWedge ? "8 3" : "2 5"} vectorEffect="non-scaling-stroke" filter={l.strong || isWedge ? "url(#glow)" : undefined} />
                <text x={3} y={yy - 3} fill={col} fontSize="9.5" fontFamily="JetBrains Mono">{label} {fmt(l.price, dec(l.price))}</text>
              </g>
            );
          })}

          {/* wedge / channel diagonal rails (the actual forming pattern) */}
          {wedge && (() => {
            const group = tfMin / 5;
            const span = Math.max(1, Math.round((wedge.span_bars || 60) / group));
            const kR = view.total - 1;      // latest bar (full-array index)
            const kL = kR - span;           // detection-window start
            const xAt = (k) => view.xOfGlobal(k);
            const xR = xAt(kR), xL = xAt(kL);
            const yTS = view.y(wedge.top_start), yTE = view.y(wedge.top_end);
            const yBS = view.y(wedge.bottom_start), yBE = view.y(wedge.bottom_end);
            const veto = wedge.veto;
            const railCol = "#f59e0b";
            const biasCol = veto ? "#f43f5e" : wedge.bias === "LONG" ? "#22c55e" : wedge.bias === "SHORT" ? "#f43f5e" : "#f59e0b";
            const labelTxt = `${(wedge.kind || "wedge").toUpperCase()}${wedge.bias ? " · " + wedge.bias : ""}${veto ? " · VETO" : ""}`;
            return (
              <g filter="url(#glow)" data-testid="wedge-rails">
                <polygon points={`${xL},${yTS} ${xR},${yTE} ${xR},${yBE} ${xL},${yBS}`} fill={railCol} opacity="0.07" />
                <line x1={xL} y1={yTS} x2={xR} y2={yTE} stroke={railCol} strokeWidth="1.5" strokeDasharray="7 4" opacity="0.9" vectorEffect="non-scaling-stroke" />
                <line x1={xL} y1={yBS} x2={xR} y2={yBE} stroke={railCol} strokeWidth="1.5" strokeDasharray="7 4" opacity="0.9" vectorEffect="non-scaling-stroke" />
                <text x={Math.max(4, xL + 4)} y={Math.min(yTS, yTE) - 5} fill={biasCol} fontSize="10" fontWeight="700" fontFamily="JetBrains Mono">{labelTxt}</text>
              </g>
            );
          })()}


          {/* user-drawn trendlines (anchored by timestamp so they stay put on zoom/pan) */}
          {lines.map((ln, i) => (
            <line key={`tl-${i}`} data-testid={`trendline-${i}`}
              x1={tsToX(ln.a.ts)} y1={view.y(ln.a.price)} x2={tsToX(ln.b.ts)} y2={view.y(ln.b.price)}
              stroke="#38bdf8" strokeWidth="1.6" opacity="0.9" vectorEffect="non-scaling-stroke" />
          ))}
          {pending && (
            <circle cx={tsToX(pending.ts)} cy={view.y(pending.price)} r="3.5" fill="#38bdf8"
              stroke="#0ea5e9" strokeWidth="1" data-testid="trendline-pending" />
          )}

          {/* ---- LINE / HYBRID price stream (close path) — LIQUID is drawn on the canvas layer ---- */}
          {(mode === "LINE" || mode === "HYBRID") && view.data.length > 1 && (() => {
            const pts = view.data.map((b, i) => [view.xOfLocal(i), view.y(b.c)]);
            const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
            const up = (market?.change || 0) >= 0;
            const col = up ? "#26d07c" : "#ff4d6d";
            const area = `${path} L ${pts[pts.length - 1][0].toFixed(1)} ${PADT + PLOTH} L ${pts[0][0].toFixed(1)} ${PADT + PLOTH} Z`;
            return (
              <g clipPath="url(#plotClip)">
                {mode !== "LINE" && (
                  <>
                    <defs>
                      <linearGradient id="liquidfill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={col} stopOpacity="0.38" />
                        <stop offset="55%" stopColor={col} stopOpacity="0.12" />
                        <stop offset="100%" stopColor={col} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d={area} fill="url(#liquidfill)" />
                  </>
                )}
                <path d={path} fill="none" stroke={col} strokeWidth={mode === "LINE" ? "1.4" : "2.1"}
                  strokeLinejoin="round" strokeLinecap="round" filter={mode === "LINE" ? undefined : "url(#glow)"}
                  vectorEffect="non-scaling-stroke" opacity={mode === "HYBRID" ? 0.85 : 1} />
              </g>
            );
          })()}

          {/* candles (CANDLE + HYBRID) */}
          {(mode === "CANDLE" || mode === "HYBRID") && (
          <g clipPath="url(#plotClip)">
          {view.data.map((b, i) => {
            const x = view.xOfLocal(i);
            const up = b.c >= b.o;
            const col = up ? "#26d07c" : "#ff4d6d";
            const bw = Math.max(view.cw * 0.64, 1.4);
            const oy = view.y(b.o), cy = view.y(b.c);
            const top = Math.min(oy, cy);
            const bh = Math.max(Math.abs(cy - oy), 0.8);
            const last = i === view.data.length - 1;
            const op = mode === "HYBRID" ? 0.4 : (last ? 1 : 0.92);
            return (
              <g key={i} filter={last && mode === "CANDLE" ? "url(#glow)" : undefined} opacity={op}>
                <line x1={x} x2={x} y1={view.y(b.h)} y2={view.y(b.l)} stroke={col} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                <rect x={x - bw / 2} y={top} width={bw} height={bh} fill={col} rx="0.5" />
              </g>
            );
          })}
          </g>
          )}

          {/* ---- THREE PROBABILISTIC FUTURE PATHS (cones) — projected into the reserved future zone ---- */}
          {layers.scenario && forecast && forecast.scenarios && view.data.length > 2 && (() => {
            const anchor = forecast.anchor_price;
            if (anchor == null || anchor < view.lo || anchor > view.hi) return null;
            const x0 = view.xOfGlobal(view.total - 1);          // latest real bar = NOW
            if (x0 > view.plotW - 24 || x0 < 20) return null;    // only when NOW sits in the past region
            const yA = view.y(anchor);
            const xEnd = view.plotW - 4;
            const span = Math.max(24, xEnd - x0);
            const cmap = { BULL: "#22c55e", BASE: "#a1a1aa", BEAR: "#f43f5e" };
            const clampY = (p) => view.y(Math.max(view.lo, Math.min(view.hi, p)));
            const ordered = [...forecast.scenarios].sort((a, b) => (a.type === forecast.primary ? 1 : 0) - (b.type === forecast.primary ? 1 : 0));
            return (
              <g data-testid="future-cones">
                {/* future-zone shading + NOW divider */}
                <rect x={x0} y={PADT} width={xEnd - x0} height={PLOTH} fill="#0a0f18" opacity="0.55" />
                <line x1={x0} y1={PADT} x2={x0} y2={PADT + PLOTH} stroke="#38BDF8" strokeWidth="1" opacity="0.55" vectorEffect="non-scaling-stroke" />
                <rect x={x0} y={PADT + 2} width={30} height={12} rx="2" fill="#38BDF8" />
                <text x={x0 + 15} y={PADT + 11} textAnchor="middle" fill="#04070c" fontSize="8" fontWeight="800" fontFamily="JetBrains Mono">NOW</text>
                {ordered.map((sc) => {
                  const c = cmap[sc.type];
                  const primary = sc.type === forecast.primary;
                  const yT = clampY(sc.target);
                  const rHi = sc.range ? clampY(sc.range[1]) : yT;
                  const rLo = sc.range ? clampY(sc.range[0]) : yT;
                  const cx = x0 + span * 0.55;
                  const band = `M ${x0} ${yA} Q ${cx} ${(yA + rHi) / 2} ${xEnd} ${rHi} L ${xEnd} ${rLo} Q ${cx} ${(yA + rLo) / 2} ${x0} ${yA} Z`;
                  const line = `M ${x0} ${yA} Q ${cx} ${(yA + yT) / 2} ${xEnd} ${yT}`;
                  const op = 0.08 + (sc.prob / 100) * 0.16;
                  const lyOff = sc.type === "BULL" ? -4 : sc.type === "BEAR" ? 11 : (yT <= yA ? -4 : 11);
                  return (
                    <g key={sc.type}>
                      <path d={band} fill={c} opacity={primary ? op + 0.05 : op} />
                      <path d={line} fill="none" stroke={c} strokeWidth={primary ? "2.4" : "1.3"}
                        strokeDasharray={primary ? "6 3" : "3 4"} opacity={primary ? 1 : 0.55}
                        filter={primary ? "url(#glow)" : undefined} vectorEffect="non-scaling-stroke" />
                      <circle cx={xEnd} cy={yT} r={primary ? 3 : 2} fill={c} opacity={primary ? 1 : 0.7} filter={primary ? "url(#glow)" : undefined} />
                      <text x={xEnd - 4} y={yT + lyOff} textAnchor="end"
                        fill={c} fontSize={primary ? "10" : "8.5"} fontWeight="700" fontFamily="JetBrains Mono" opacity={primary ? 1 : 0.8}>
                        {sc.type} {sc.prob}% · {fmt(sc.target, dec(sc.target))}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })()}


          {/* live price line + pill */}
          {livePrice != null && livePrice >= view.lo && livePrice <= view.hi && (() => {
            const yy = view.y(livePrice);
            const up = (market?.change || 0) >= 0;
            const col = up ? "#26d07c" : "#ff4d6d";
            return (
              <g>
                <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke={col} strokeWidth="1" strokeDasharray="1 4" opacity="0.5" vectorEffect="non-scaling-stroke" />
                <rect x={view.plotW} y={yy - 8} width={PADR} height={16} fill={col} rx="2" />
                <text x={view.plotW + 5} y={yy + 3.5} fill="#04070c" fontSize="10.5" fontWeight="700" fontFamily="JetBrains Mono">{fmt(livePrice, dec(livePrice))}</text>
              </g>
            );
          })()}

          {/* STOP TRAIL — every stop move as a step line through time; REPLAY animates it */}
          {position && hist.length > 1 && (() => {
            const upto = replay == null ? hist.length : replay + 1;
            const pts = hist.slice(0, upto).map((h) => ({ x: Math.max(0, Math.min(view.plotW, tsToX(h.t))), y: view.y(Math.max(view.lo, Math.min(view.hi, h.px))), h }));
            let d = "";
            pts.forEach((pt, i) => { if (i === 0) d += `M${pt.x},${pt.y}`; else d += ` H${pt.x} V${pt.y}`; });
            const last = pts[pts.length - 1];
            if (replay == null) d += ` H${view.plotW}`;
            return (
              <g data-testid="stop-trail" style={{ pointerEvents: "none" }}>
                <path d={d} fill="none" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="3 3" opacity={replay == null ? 0.55 : 0.95} />
                {pts.map((pt, i) => <circle key={i} cx={pt.x} cy={pt.y} r={i === pts.length - 1 && replay != null ? 4 : 2} fill={pt.h.src === "initial" ? "#71717a" : "#f59e0b"} opacity="0.9" />)}
                {replay != null && last && (
                  <g>
                    <line x1={0} x2={view.plotW} y1={last.y} y2={last.y} stroke="#f59e0b" strokeWidth="1.5" opacity="0.9" />
                    <text x={last.x + 6} y={last.y - 6} fill="#f59e0b" fontSize="9" fontWeight="800" fontFamily="JetBrains Mono" data-testid="stop-replay-label">
                      STOP → {last.h.px.toFixed(position.point_value >= 1 ? 2 : 1)} · {new Date(last.h.t * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {last.h.src} · {replay + 1}/{hist.length}
                    </text>
                  </g>
                )}
              </g>
            );
          })()}

          {/* ENTRY / SL / TP markers (drawn above candles, draggable) */}
          {position && (() => {
            const slP = preview?.which === "sl" ? preview.price : (replay != null && hist[replay] ? hist[replay].px : position.stop_loss);
            const tpP = preview?.which === "tp" ? preview.price : position.take_profit;
            const pnlTag = position.pnl != null ? ` ${position.pnl >= 0 ? "+" : "-"}$${Math.abs(position.pnl).toFixed(2)}` : "";
            const trailing = position.init_stop && slP && Math.abs(slP - position.init_stop) > 1e-9;
            const items = [
              { key: "entry", p: position.entry_price, c: position.pnl > 0 ? "#00E5B5" : position.pnl < 0 ? "#FF3355" : "#e4e4e7", t: `${position.direction || ""} ×${position.qty || 1}${pnlTag}`, drag: false },
              { key: "sl", p: slP, c: trailing ? "#f59e0b" : "#ff4d6d", t: trailing ? "TRAIL" : "SL", drag: true },
              { key: "tp", p: tpP, c: "#38bdf8", t: "TP", drag: true },
              ...(trailing ? [{ key: "isl", p: position.init_stop, c: "#71717a", t: "INIT SL", drag: false, faint: true }] : []),
            ];
            return items.map((o) => {
              if (!o.p || o.p < view.lo || o.p > view.hi) return null;
              const yy = view.y(o.p);
              const active = preview?.which === o.key;
              const canDrag = o.drag && adjustable;
              return (
                <g key={o.key} filter="url(#glow)">
                  <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke={o.c} strokeWidth={active ? 1.9 : o.faint ? 0.8 : 1.2} opacity={active ? 1 : o.faint ? 0.5 : 0.9} strokeDasharray={o.drag || o.faint ? "7 4" : undefined} vectorEffect="non-scaling-stroke" />
                  <rect x={view.plotW} y={yy - 8} width={PADR} height={16} fill={o.c} rx="2" opacity={o.faint ? 0.6 : 0.95} />
                  <text x={view.plotW + 4} y={yy + 3.5} fill="#04070c" fontSize="9.5" fontWeight="700" fontFamily="JetBrains Mono" data-testid={`pos-mark-${o.key}`}>{o.key === "entry" ? "" : `${o.t} `}{fmt(o.p, dec(o.p))}</text>
                  {o.key === "entry" && <text x={view.plotW - 6} y={yy - 5} textAnchor="end" fill={o.c} fontSize="9" fontWeight="800" fontFamily="JetBrains Mono" data-testid="pos-mark-entry-label">ENTRY · {o.t}</text>}
                  {canDrag && (
                    <>
                      <rect x={0} y={yy - 6} width={view.plotW} height={12} fill="transparent" style={{ cursor: "ns-resize" }} onPointerDown={startDrag(o.key)} data-testid={`drag-${o.key}-line`} />
                      <circle cx={view.plotW - 4} cy={yy} r="4" fill={o.c} style={{ cursor: "ns-resize" }} onPointerDown={startDrag(o.key)} data-testid={`drag-${o.key}-handle`} />
                    </>
                  )}
                </g>
              );
            });
          })()}

          {/* ---- Reversal setup: change-of-character level (sweep + entry/stop/target drawn by their own layers) ---- */}
          {layers.sweep && rev && rev.choch_level != null && rev.choch_level >= view.lo && rev.choch_level <= view.hi && (() => {
            const yy = view.y(rev.choch_level);
            const col = "#a78bfa";
            return (
              <g data-testid="choch-marker">
                <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke={col} strokeWidth="1.3" strokeDasharray="2 3" opacity="0.95" vectorEffect="non-scaling-stroke" />
                <text x={view.pastW * 0.5} y={yy - 4} fill={col} fontSize="9.5" fontWeight="700" fontFamily="JetBrains Mono">CHoCH {rev.direction === "LONG" ? "▲" : "▼"} {fmt(rev.choch_level, dec(rev.choch_level))}</text>
              </g>
            );
          })()}

          {/* ---- Liquidity sweep + predicted reversal ---- */}
          {layers.sweep && sweep && sweep.level >= view.lo && sweep.level <= view.hi && (() => {
            const yy = view.y(sweep.level);
            const up = sweep.side === "LONG"; // sell-side sweep -> reversal UP
            const col = "#eab308";
            const ax = view.plotW - 42;
            const ay = up ? yy + 22 : yy - 22;
            return (
              <g filter="url(#glow)" data-testid="sweep-marker">
                <line x1={0} x2={view.plotW} y1={yy} y2={yy} stroke={col} strokeWidth="1.3" strokeDasharray="6 3" opacity="0.9" vectorEffect="non-scaling-stroke" />
                <text x={3} y={yy - 4} fill={col} fontSize="9.5" fontWeight="700" fontFamily="JetBrains Mono">⚡ SWEEP · rev {up ? "▲" : "▼"} {fmt(sweep.level, dec(sweep.level))}</text>
                <line x1={ax} y1={yy} x2={ax} y2={ay} stroke={col} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                <path d={`M ${ax} ${ay} l -4 ${up ? 6 : -6} l 8 0 z`} fill={col} />
              </g>
            );
          })()}

          {/* ---- Potential-entry scenario: projected target (win) & invalidation (stop) ---- */}
          {layers.scenario && scenario && (() => {
            const yE = view.y(Math.max(view.lo, Math.min(view.hi, scenario.entry)));
            const yT = view.y(Math.max(view.lo, Math.min(view.hi, scenario.tp)));
            const yS = view.y(Math.max(view.lo, Math.min(view.hi, scenario.sl)));
            const long = scenario.dir === "LONG";
            const green = "#22c55e", red = "#f43f5e";
            const inRange = (p) => p >= view.lo && p <= view.hi;
            return (
              <g data-testid="scenario-projection">
                {/* target outcome band (entry -> TP) */}
                <rect x={0} y={Math.min(yE, yT)} width={view.plotW} height={Math.abs(yT - yE)} fill={green} opacity="0.08" />
                {/* invalidation band (entry -> SL) */}
                <rect x={0} y={Math.min(yE, yS)} width={view.plotW} height={Math.abs(yS - yE)} fill={red} opacity="0.08" />
                {inRange(scenario.tp) && (
                  <g filter="url(#glow)">
                    <line x1={0} x2={view.plotW} y1={yT} y2={yT} stroke={green} strokeWidth="1.3" strokeDasharray="7 4" opacity="0.9" vectorEffect="non-scaling-stroke" />
                    <text x={3} y={yT - 3} fill={green} fontSize="9" fontWeight="700" fontFamily="JetBrains Mono">TARGET {fmt(scenario.tp, dec(scenario.tp))}</text>
                  </g>
                )}
                {inRange(scenario.sl) && (
                  <g filter="url(#glow)">
                    <line x1={0} x2={view.plotW} y1={yS} y2={yS} stroke={red} strokeWidth="1.3" strokeDasharray="7 4" opacity="0.9" vectorEffect="non-scaling-stroke" />
                    <text x={3} y={yS + 10} fill={red} fontSize="9" fontWeight="700" fontFamily="JetBrains Mono">INVALIDATION {fmt(scenario.sl, dec(scenario.sl))}</text>
                  </g>
                )}
                {inRange(scenario.entry) && (
                  <g>
                    <line x1={0} x2={view.plotW} y1={yE} y2={yE} stroke="#e4e4e7" strokeWidth="1" strokeDasharray="2 3" opacity="0.7" vectorEffect="non-scaling-stroke" />
                    {/* direction arrow near right edge */}
                    <line x1={view.plotW - 20} y1={yE} x2={view.plotW - 20} y2={long ? yE - 20 : yE + 20} stroke={long ? green : red} strokeWidth="2.4" filter="url(#glow)" vectorEffect="non-scaling-stroke" />
                    <path d={`M ${view.plotW - 20} ${long ? yE - 20 : yE + 20} l -5 ${long ? 7 : -7} l 10 0 z`} fill={long ? green : red} filter="url(#glow)" />
                  </g>
                )}
                {/* scenario badge */}
                <g>
                  <rect x={8} y={PADT + 6} width={172} height={20} rx="3" fill="#0b0f16" stroke={long ? green : red} strokeOpacity="0.6" />
                  <circle cx={20} cy={PADT + 16} r="3" fill={long ? green : red} className="pulse" />
                  <text x={30} y={PADT + 19.5} fill={long ? green : red} fontSize="9.5" fontWeight="700" fontFamily="JetBrains Mono">
                    {scenario.triggered ? "ENTRY" : "SETUP"} {scenario.dir} · {scenario.grade}{scenario.rr ? ` · ${scenario.rr}R` : ""}
                  </text>
                </g>
              </g>
            );
          })()}

          {/* ---- Desk prognosis: persistent call, invalidation, target, liquidity marks ---- */}
          <DeskMarks prognosis={layers.desk && prognosis && prognosis.symbol === symbol ? prognosis : null} view={view} bars={bars} PADT={PADT} PLOTH={PLOTH}
            trend={layers.desk && trend && trend.symbol === symbol ? trend : null} deskOrder={deskOrder && deskOrder.symbol === symbol ? deskOrder : null}
            positions={(pme?.positions || []).filter((m) => m.symbol === symbol)} follow={layers.desk ? follow : null} />

          {/* crosshair */}
          {cursor && (
            <g pointerEvents="none">
              <line x1={cursor.x} x2={cursor.x} y1={PADT} y2={H - PADB} stroke="#3b4453" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              <line x1={0} x2={view.plotW} y1={cursor.y} y2={cursor.y} stroke="#3b4453" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              <rect x={view.plotW} y={cursor.y - 8} width={PADR} height={16} fill="#3b4453" rx="2" />
              <text x={view.plotW + 5} y={cursor.y + 3.5} fill="#e4e4e7" fontSize="10.5" fontFamily="JetBrains Mono">{fmt(cursor.price, dec(cursor.price))}</text>
            </g>
          )}
        </svg>
      )}

      {/* Reversal setup card (sweep → CHoCH → entry/stop/target) */}
      {rev && !cursor?.bar && (
        <div data-testid="reversal-badge" className="absolute top-9 left-2 z-10 font-mono text-[10px] bg-black/70 backdrop-blur-sm border border-violet-500/40 rounded px-2 py-1.5 pointer-events-none space-y-0.5">
          <div className="font-bold tracking-wider text-violet-300">REVERSAL SETUP {rev.direction === "LONG" ? "▲ LONG" : "▼ SHORT"} · <span className={rev.confirmed ? "text-emerald-400" : "text-amber-300"}>{rev.confirmed ? "CONFIRMED" : "WAITING"}</span></div>
          <div className="flex gap-2 text-zinc-300">
            <span className={rev.sweep ? "text-yellow-300" : "text-zinc-500"}>{rev.sweep ? "✓" : "○"} sweep{rev.sweep_level != null ? ` ${fmt(rev.sweep_level, dec(rev.sweep_level))}` : ""}</span>
            <span className={rev.choch ? "text-violet-300" : "text-zinc-500"}>{rev.choch ? "✓" : "○"} CHoCH{rev.choch_level != null ? ` ${fmt(rev.choch_level, dec(rev.choch_level))}` : ""}</span>
          </div>
          {rev.stop_loss != null && rev.take_profit != null && (
            <div className="flex gap-2 text-zinc-400">
              <span>entry <span className="text-zinc-100">{fmt(rev.entry, dec(rev.entry))}</span></span>
              <span>stop <span className="text-rose-400">{fmt(rev.stop_loss, dec(rev.stop_loss))}</span></span>
              <span>target <span className="text-emerald-400">{fmt(rev.take_profit, dec(rev.take_profit))}</span></span>
            </div>
          )}
        </div>
      )}

      {/* OHLC readout */}
      {cursor?.bar && (
        <div className="absolute top-9 left-2 z-10 font-mono text-[10px] text-zinc-400 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1 pointer-events-none flex gap-2">
          <span>O <span className="text-zinc-200">{fmt(cursor.bar.o, dec(cursor.bar.o))}</span></span>
          <span>H <span className="text-emerald-400">{fmt(cursor.bar.h, dec(cursor.bar.h))}</span></span>
          <span>L <span className="text-rose-400">{fmt(cursor.bar.l, dec(cursor.bar.l))}</span></span>
          <span>C <span className="text-zinc-200">{fmt(cursor.bar.c, dec(cursor.bar.c))}</span></span>
        </div>
      )}
    </div>
  );
};
